import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import FilterPanelRadioGroup from 'components/Common/FilterPanel/FilterPanelRadioGroup'
import type { FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import { useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'

export const BEDROOMS_RADIO_ANY = 0
export const BEDROOMS_RADIO_STUDIO_OR_1 = 1
export const BEDROOMS_RADIO_2 = 2
export const BEDROOMS_RADIO_3_PLUS = 3

/** Default treatment order: Any → Studio/1 → 2 → 3+. */
export const BEDROOMS_RADIO_ORDER_DEFAULT = [
  BEDROOMS_RADIO_ANY,
  BEDROOMS_RADIO_STUDIO_OR_1,
  BEDROOMS_RADIO_2,
  BEDROOMS_RADIO_3_PLUS,
] as const

/** Family treatment order: Any → 3+ → 2 → Studio/1. */
export const BEDROOMS_RADIO_ORDER_WITH_CHILDREN = [
  BEDROOMS_RADIO_ANY,
  BEDROOMS_RADIO_3_PLUS,
  BEDROOMS_RADIO_2,
  BEDROOMS_RADIO_STUDIO_OR_1,
] as const

interface Props {
  loading?: boolean
  bedroomsGte?: number
  bedroomsEq?: number
  /** When true, list radios as Any → 3+ → 2 → Studio/1. */
  useFamilyOptionOrder?: boolean
  onChange: (value: number) => void
}

/**
 * Studio/1 and 2 are exact (`bedroomsEq`). `bedroomsGte <= 1` is treated as Any
 * (near no-op under ≥ semantics). 3+ uses `bedroomsGte`; `bedroomsGte=2` deep-links
 * still select the 2-bedroom radio for backwards compatibility.
 */
export function getSelectedBedroomsRadioValue(bedroomsGte?: number, bedroomsEq?: number): number {
  if (bedroomsEq === 1) return BEDROOMS_RADIO_STUDIO_OR_1
  if (bedroomsEq === 2) return BEDROOMS_RADIO_2

  if (bedroomsGte && bedroomsGte >= 2) {
    if (bedroomsGte === 2) return BEDROOMS_RADIO_2
    return BEDROOMS_RADIO_3_PLUS
  }

  if (bedroomsEq && bedroomsEq >= 3) return BEDROOMS_RADIO_3_PLUS

  return BEDROOMS_RADIO_ANY
}

export function getBedroomsRadioOrder(useFamilyOptionOrder?: boolean): ReadonlyArray<number> {
  return useFamilyOptionOrder ? BEDROOMS_RADIO_ORDER_WITH_CHILDREN : BEDROOMS_RADIO_ORDER_DEFAULT
}

function BedroomsRadioFilterGroup({
  loading,
  bedroomsGte,
  bedroomsEq,
  useFamilyOptionOrder = false,
  onChange,
}: Props) {
  const intl = useIntl()

  const bedroomsTitle = intl.formatMessage({
    id: 'kBhZ0iBX2U0KePMQ8JUX',
    defaultMessage: 'Bedrooms',
  })

  const anyLabel = intl.formatMessage({
    id: 'l4InBcZkvlsq6R--Wv5n',
    defaultMessage: 'Any',
  })

  const studioOrOneLabel = intl.formatMessage({
    id: 'gt-c0zLR83nMfIcFrED3',
    defaultMessage: 'Studio or 1 bedroom',
  })

  const twoBedroomsLabel = intl.formatMessage({
    id: 'A7jwYiy86F7JdMjsXmv8',
    defaultMessage: '2 bedrooms',
  })

  const threePlusLabel = intl.formatMessage({
    id: 'kOJPP5MzYg1oyT_O-B1L',
    defaultMessage: '3+ bedrooms',
  })

  const items = useMemo((): Array<FilterPanelCheckItem> => {
    const labelsByValue: Record<number, string> = {
      [BEDROOMS_RADIO_ANY]: anyLabel,
      [BEDROOMS_RADIO_STUDIO_OR_1]: studioOrOneLabel,
      [BEDROOMS_RADIO_2]: twoBedroomsLabel,
      [BEDROOMS_RADIO_3_PLUS]: threePlusLabel,
    }

    return getBedroomsRadioOrder(useFamilyOptionOrder).map((value) => ({
      value,
      label: labelsByValue[value],
    }))
  }, [anyLabel, studioOrOneLabel, twoBedroomsLabel, threePlusLabel, useFamilyOptionOrder])

  const selected = getSelectedBedroomsRadioValue(bedroomsGte, bedroomsEq)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(Number(e.target.value))
  }

  return (
    <FilterPanelFilterGroup
      loading={loading}
      title={bedroomsTitle}
    >
      <FilterPanelRadioGroup
        name="bedrooms"
        items={items}
        selected={selected}
        onChange={handleChange}
      />
    </FilterPanelFilterGroup>
  )
}

export default BedroomsRadioFilterGroup
